import { useEffect, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { setUser } from '@sentry/react'
import { useLocation } from 'wouter'
import { useShallow } from 'zustand/react/shallow'

import queryClient from '@/queryClient'
import {
  verifyLogin,
  login,
  logout,
  signUp,
  setCookie,
  getCookie,
  getToken,
} from '@/lib/api/client'
import { LOGIN_REDIRECT_NAME, BASE_URL } from '@/constants'
import QUERY_KEYS from './queryKeys'
import MUTATION_KEYS from './mutationKeys'
import { useAppStore } from '@/state'
import { useJwtToken } from '@/external/onshape/hooks/useJwtToken'
import { JWT_TOKEN_COOKIE_NAME } from '@/external/onshape/constants'

type useVerifyLoginProps = {
  enabled?: boolean
  onSuccess?: () => void
  jwtAuth?: boolean
}

const defaultUseVerifyLoginProps: useVerifyLoginProps = {
  enabled: true,
}

const verifyLoginQueryFn = async (
  onSuccess?: () => void,
  jwtToken?: string | null,
) => {
  const resp = await verifyLogin(jwtToken)

  if (resp && resp.response.ok && onSuccess) {
    onSuccess()
  }

  return {
    is_logged_in: resp && resp.response.ok,
    user: resp ? resp.data : null,
  }
}

export const useVerifyLogin = (props?: useVerifyLoginProps) => {
  const { enabled, onSuccess } = {
    ...defaultUseVerifyLoginProps,
    ...props,
  }
  const onUserLogin = useAppStore((state) => state.onUserLogin)
  const jwtToken = useJwtToken()

  const { data, isLoading, isError, refetch } = useQuery({
    enabled,
    refetchOnMount: true,
    staleTime: 0,
    gcTime: 0,
    queryKey: [QUERY_KEYS.VERIFY_LOGIN],
    queryFn: () => verifyLoginQueryFn(onSuccess, jwtToken),
  })

  const isLoggedIn = useMemo(
    () => !isLoading && data?.is_logged_in,
    [isLoading, data?.is_logged_in],
  )

  useEffect(() => {
    if (isLoggedIn && data?.user) {
      onUserLogin(data.user)
    }
  }, [isLoggedIn, data?.user, onUserLogin])

  return {
    isLoggedIn,
    user: data?.user,
    isVerifyingLoginStatus: isLoading,
    loginVerificationFailed: isError,
    refetchVerifyLogin: refetch,
  }
}

export const verifyLoginQuery = (props?: useVerifyLoginProps) =>
  queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.VERIFY_LOGIN],
    queryFn: () => verifyLoginQueryFn(props?.onSuccess),
  })

interface LoginParams {
  redirectUrl?: string
  jwtAuth?: boolean
}

export const useLogin = (params?: LoginParams) => {
  const [, setLocation] = useLocation()

  const setJwtToken = useAppStore(useShallow((state) => state.setJwtToken))

  const { mutate, data, isPending, isError, error } = useMutation({
    mutationKey: [MUTATION_KEYS.LOGIN],
    mutationFn: async (values: { email: string; password: string }) => {
      let loginSuccessful = false
      let loginRedirectUrl = params?.redirectUrl || '~/'
      let jwtToken: string | null = null

      try {
        if (params?.jwtAuth) {
          const jwtResp = await getToken(values.email, values.password)
          loginSuccessful = jwtResp.response.ok

          if (loginSuccessful) {
            jwtToken = jwtResp.data.token
            setJwtToken(jwtToken)
            setCookie(JWT_TOKEN_COOKIE_NAME, jwtToken, {
              maxAge: 55 * 60, // expires in 55 minutes
            })
          }
        } else {
          const loginResp = await login(values.email, values.password)

          loginSuccessful = loginResp.response.ok
          loginRedirectUrl = getCookie(LOGIN_REDIRECT_NAME) || '~/'
        }

        const verifyResp = await verifyLogin(jwtToken)
        const isLoggedIn = Boolean(verifyResp && verifyResp.response.ok)

        if (!isLoggedIn && !loginRedirectUrl) {
          throw new Error('Invalid login credentials')
        }

        return {
          loginSuccessful,
          isLoggedIn: isLoggedIn,
          loginRedirectUrl,
        }
      } catch (err) {
        if (err instanceof Error) {
          throw err
        }
        throw new Error('Unknown error')
      }
    },
    onSuccess: ({ isLoggedIn, loginRedirectUrl }) => {
      if (isLoggedIn) {
        const continueToSearchParam = new URLSearchParams(
          window.location.search,
        ).get('continue_to')
        const isContinuingToLogin = (continueToSearchParam || '').includes(
          '/login',
        )

        const defaultRoot = params?.redirectUrl || '~/'

        const continueToUrl = isContinuingToLogin
          ? defaultRoot
          : continueToSearchParam || defaultRoot

        setLocation(continueToUrl)
        return
      }

      if (!isLoggedIn && loginRedirectUrl) {
        window.location.href = JSON.parse(loginRedirectUrl)
      }
    },
  })

  return {
    handleLogin: mutate,
    isLogginIn: isPending,
    isLoggedIn: data?.loginSuccessful,
    loginFailed: isError,
    error,
  }
}

interface LogoutParams {
  loginUrl?: string
}

export const useLogout = (params?: LogoutParams) => {
  const [, setLocation] = useLocation()
  const onUserLogout = useAppStore((state) => state.onUserLogout)
  const { mutate, data, isPending, isError } = useMutation({
    mutationKey: [MUTATION_KEYS.LOGOUT],
    mutationFn: async () => {
      const logoutResp = await logout()
      const verifyResp = await verifyLogin()
      const isLoggedIn = verifyResp && verifyResp.response.ok

      if (logoutResp.response.ok && !isLoggedIn) {
        setLocation(params?.loginUrl || '~/login')
      } else {
        throw new Error('Logout failed!')
      }

      return {
        logOutSuccessful: !!logoutResp.response.ok && !isLoggedIn,
      }
    },
    onSuccess: () => {
      onUserLogout()
      queryClient.clear()
    },
  })

  useEffect(() => {
    if (data?.logOutSuccessful) {
      setUser(null)
    }
  }, [data?.logOutSuccessful])

  return {
    handleLogout: mutate,
    isLogginOut: isPending,
    isLoggedOut: data?.logOutSuccessful,
    logoutFailed: isError,
  }
}

export const useSignUp = () => {
  const [, setLocation] = useLocation()
  return useMutation({
    mutationKey: [MUTATION_KEYS.LOGIN],
    mutationFn: async (values: {
      email: string
      password: string
      firstName: string
      lastName: string
      role: string
    }) => signUp(values),

    onSuccess: () => {
      const verifyEmailUrl = `${BASE_URL}/accounts/confirm-email/`
      window.location.href = verifyEmailUrl
    },

    onError: (error) => {
      setLocation(`~/login?error=${encodeURIComponent(error.message)}`)
    },
  })
}

export { QUERY_KEYS, MUTATION_KEYS }
